import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { CheckCircle, Trash3, Eye } from "react-bootstrap-icons";
import { Button } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { useApi } from "../../Controller/ApiController";

interface Notification {
  id: number;
  data: {
    message: string;
    email: string;
    mobile_number: number;
    visitorId: number;
  };
  read_at: Date | null;
}

interface UnreadNotificationsProps {
  onMarkAsRead: (notificationId: number) => void;
  onDelete: (notificationId: number) => void;
  viewNotificationData: (message: any) => void;
}

const UnreadNotifications = forwardRef(
  (
    { onMarkAsRead, onDelete, viewNotificationData }: UnreadNotificationsProps,
    ref,
  ) => {
    const { apiGet, apiPost, apiDelete } = useApi();
    const [unreadNotifications, setUnreadNotifications] = useState<
      Notification[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const LIMIT = 10;

    const fetchUnreadNotifications = async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await apiPost(
          `notifications/search?page=${page}&limit=${LIMIT}`,
          {
            filters: [{ field: "read_at", operator: "=", value: null }],
            sort: [{ field: "created_at", direction: "desc" }],
          },
        );

        const fetched = response.data || [];

        if (page === 1) {
          setUnreadNotifications(fetched);
        } else {
          setUnreadNotifications((prev) => [...prev, ...fetched]);
        }

        setHasMore(fetched.length === LIMIT);
      } catch (error) {
        console.error("Error fetching unread notifications:", error);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      fetchUnreadNotifications();
      // eslint-disable-next-line
    }, []);
    useImperativeHandle(ref, () => ({
      fetchUnreadNotifications,
    }));
    const markNotificationAsRead = async (notificationId: number) => {
      try {
        await apiGet(`notification/read/${notificationId}`);
        setUnreadNotifications((prev) =>
          prev.filter((notification) => notification.id !== notificationId),
        );
        onMarkAsRead(notificationId);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    };

    const deleteNotificationData = async (notificationId: number) => {
      try {
        await apiDelete(`notifications/${notificationId}`);
        setUnreadNotifications((prev) =>
          prev.filter((notification) => notification.id !== notificationId),
        );
        onDelete(notificationId);
      } catch (error) {
        console.error("Error deleting notification:", error);
      }
    };

    const loadMore = () => {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchUnreadNotifications(nextPage);
    };

    const formatMessage = (message: string) => {
      const snakeRegex = /^([a-z_]+)\s/;
      const match = message.match(snakeRegex);

      if (match) {
        const formatted = match[1]
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        return message.replace(snakeRegex, `${formatted} `);
      }

      return message;
    };

    return (
      <div className="p-0">
        {isLoading && currentPage === 1 ? (
          <Skeleton count={8} height={55} />
        ) : unreadNotifications.length > 0 ? (
          <>
            {unreadNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`d-flex justify-content-between align-items-center p-3 notification-item ${
                  !notification.read_at ? "unread" : ""
                }`}
              >
                <div className="d-flex flex-column">
                  <small className="text-muted">
                    {formatMessage(notification.data.message)}
                  </small>
                </div>

                <div className="d-flex align-items-center">
                  {!notification.read_at && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      className="me-2"
                      onClick={() => markNotificationAsRead(notification.id)}
                    >
                      <CheckCircle size={15} className="me-1" /> Read
                    </Button>
                  )}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => viewNotificationData(notification.data)}
                  >
                    <Eye size={15} className="me-1" /> View
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteNotificationData(notification.id)}
                  >
                    <Trash3 size={15} className="me-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="text-center mt-4 mb-5">
                <button className="btn addnew-btn btn-sm" onClick={loadMore}>
                  Load More
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-muted fs-5">No data available.</p>
          </div>
        )}
      </div>
    );
  },
);

export default UnreadNotifications;
